/**
 * Pure placement math for popovers anchored to a trigger (Combobox listbox,
 * EmpresaSelector listbox). The popover is rendered with `position: fixed`
 * from the trigger's viewport rect, so it escapes any `overflow` ancestor
 * (e.g. the modal body's `overflow-y: auto`), matches the trigger's width, and
 * flips above the trigger when there is not enough room below.
 */
export interface AnchorRect {
  left: number;
  top: number;
  bottom: number;
  width: number;
}

export interface ViewportSize {
  width: number;
  height: number;
}

export interface AnchoredPosition {
  placement: "bottom" | "top";
  left: number;
  width: number;
  /** Distance from the viewport top; only set when placed below the anchor. */
  top?: number;
  /** Distance from the viewport bottom; only set when placed above the anchor. */
  bottom?: number;
  maxHeight: number;
}

export interface AnchoredPositionOptions {
  preferredMaxHeight?: number;
  /** Space between the anchor and the popover. */
  gap?: number;
  /** Space kept between the popover and the viewport edge. */
  margin?: number;
  /** Below this much free space below the anchor, flip above if that has more. */
  flipThreshold?: number;
  /** Never shrink the popover under this height. */
  minHeight?: number;
  /**
   * Placement chosen on the previous measurement. While that side can still hold `minHeight`
   * it is kept, so an anchor drifting a few pixels (a centered modal re-centering) never
   * flips the popover above/below the field.
   */
  stickyPlacement?: "bottom" | "top";
}

export function computeAnchoredPosition(
  anchor: AnchorRect,
  viewport: ViewportSize,
  options: AnchoredPositionOptions = {},
): AnchoredPosition {
  const {
    preferredMaxHeight = 320,
    gap = 4,
    margin = 8,
    flipThreshold = 200,
    minHeight = 96,
    stickyPlacement,
  } = options;

  const spaceBelow = viewport.height - anchor.bottom - gap - margin;
  const spaceAbove = anchor.top - gap - margin;
  const naturalPlaceAbove = spaceBelow < flipThreshold && spaceAbove > spaceBelow;
  const placeAbove =
    stickyPlacement === "bottom" && spaceBelow >= minHeight
      ? false
      : stickyPlacement === "top" && spaceAbove >= minHeight
        ? true
        : naturalPlaceAbove;

  const available = placeAbove ? spaceAbove : spaceBelow;
  const maxHeight = Math.max(minHeight, Math.min(preferredMaxHeight, available));

  if (placeAbove) {
    return {
      placement: "top",
      left: anchor.left,
      width: anchor.width,
      bottom: viewport.height - anchor.top + gap,
      maxHeight,
    };
  }
  return {
    placement: "bottom",
    left: anchor.left,
    width: anchor.width,
    top: anchor.bottom + gap,
    maxHeight,
  };
}
